// components/RatingStars.tsx
import React from "react";

// Allow rating to be a fractional number
type RatingStarsProps = {
  rating: number;
  onRatingChange: (rating: number) => void;
  clearable?: boolean;
  currentColor?: string;
};

const RatingStars: React.FC<RatingStarsProps> = ({
  rating,
  onRatingChange,
  clearable = false,
  currentColor = "#333333",
}) => {
  const handleStarClick = (newRating: number) => {
    onRatingChange(newRating);
  };

  const handleClearRating = () => {
    onRatingChange(0);
  };

  const renderStars = () => {
    let stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(rating)) {
        stars.push(
          <FullStar
            key={i}
            currentColor={currentColor}
            onClick={() => handleStarClick(i)}
          />
        );
      } else if (i === Math.ceil(rating)) {
        // Calculate the fill percentage for the fractional part
        const fillPercentage = (rating % 1) * 100;
        stars.push(
          <HalfStar
            key={i}
            currentColor={currentColor}
            fillPercentage={fillPercentage}
            onClick={() => handleStarClick(i)}
          />
        );
      } else {
        stars.push(
          <EmptyStar
            key={i}
            currentColor={currentColor}
            onClick={() => handleStarClick(i)}
          />
        );
      }
    }
    return stars;
  };

  return (
    <div className="flex items-center">
      {renderStars()}

      {clearable && (
        <button
          onClick={handleClearRating}
          className="text-sm underline pl-2"
          type="button"
        >
          Clear
        </button>
      )}
    </div>
  );
};

type SvgProps = {
  onClick: () => void;
  currentColor: string;
  fillPercentage?: number;
};

const FullStar: React.FC<SvgProps> = ({ currentColor, onClick }) => (
  <svg
    className="w-5 h-5"
    fill={currentColor}
    viewBox="0 0 20 20"
    onClick={onClick}
  >
    <path d="M9.049 1.868l2.854 5.773 6.298.916-4.554 4.438 1.076 6.268-5.674-2.986-5.674 2.986 1.076-6.268-4.554-4.438 6.298-.916 2.854-5.773z" />
  </svg>
);

const HalfStar: React.FC<SvgProps> = ({
  onClick,
  fillPercentage = 0,
  currentColor,
}) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20" onClick={onClick}>
    <defs>
      <linearGradient id="half-star-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop
          offset={`${fillPercentage + 20}%`}
          style={{ stopColor: currentColor, stopOpacity: 1 }}
        />
        <stop
          offset={`${fillPercentage}%`}
          style={{ stopColor: "transparent", stopOpacity: 0 }}
        />
      </linearGradient>
    </defs>
    <path
      d="M9.049 1.868l2.854 5.773 6.298.916-4.554 4.438 1.076 6.268-5.674-2.986-5.674 2.986 1.076-6.268-4.554-4.438 6.298-.916 2.854-5.773z"
      fill="url(#half-star-gradient)"
    />
    <path
      d="M9.049 1.868l2.854 5.773 6.298.916-4.554 4.438 1.076 6.268-5.674-2.986-5.674 2.986 1.076-6.268-4.554-4.438 6.298-.916 2.854-5.773z"
      fill="none"
      stroke={currentColor}
      strokeWidth="1"
    />
  </svg>
);

const EmptyStar: React.FC<SvgProps> = ({ onClick, currentColor }) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20" onClick={onClick}>
    <path
      d="M9.049 1.868l2.854 5.773 6.298.916-4.554 4.438 1.076 6.268-5.674-2.986-5.674 2.986 1.076-6.268-4.554-4.438 6.298-.916 2.854-5.773z"
      fill="none"
      stroke={currentColor}
      strokeWidth="1"
    />
  </svg>
);

export default RatingStars;
